import React, { useState } from "react";
import { addDays, iso } from "./npdUtils";
import { errorMessage, npdApi } from "./npdApi";
import ImportParts from "./ImportParts";

const AddPart = ({ app }) => {
  const { isAdmin, me, lines, suppliers, buyers, today, onPartSaved, onSupplierAdded, openPart, toast } = app;
  // "single" = the Add Part form, "import" = many parts from an Excel file
  const [mode, setMode] = useState("single");
  const [F, setF] = useState({
    lineId: lines[0]?.id ?? "",
    name: "",
    supplierId: "",
    target: iso(addDays(today, 60)),
    stage: 0,
    buyerId: buyers[0]?.orgUserId ?? "",
    note: "",
  });
  const [err, setErr] = useState("");
  const [added, setAdded] = useState(null);
  const [saving, setSaving] = useState(false);
  // Inline "add supplier" (buyers only)
  const [newSup, setNewSup] = useState(null);
  const [supSaving, setSupSaving] = useState(false);

  const set = (k) => (e) => setF((f) => ({ ...f, [k]: e.target.value }));
  const L = lines.find((l) => String(l.id) === String(F.lineId));
  const buyerId = F.buyerId || buyers[0]?.orgUserId || "";

  const saveSupplier = async () => {
    if (!newSup?.trim()) return setErr("Enter a supplier name.");
    setSupSaving(true);
    try {
      const s = await npdApi.addSupplier(newSup.trim());
      onSupplierAdded(s);
      setF((f) => ({ ...f, supplierId: String(s.id) }));
      setNewSup(null);
      setErr("");
      toast(`${s.name} added to suppliers`);
    } catch (e) {
      setErr(errorMessage(e, "Could not add the supplier."));
    } finally {
      setSupSaving(false);
    }
  };

  const submit = async () => {
    if (!L) return setErr("Choose an assembly line.");
    if (!F.name.trim()) return setErr("Enter a part name.");
    if (!F.supplierId) return setErr("Choose a supplier.");
    if (!F.target) return setErr("Choose a target date.");
    if (F.target < iso(today)) return setErr("The target date cannot be in the past.");
    if (isAdmin && !buyerId) return setErr("Choose a buyer.");
    setErr("");
    setSaving(true);
    try {
      const p = await npdApi.addPart({
        assemblyLineId: L.id,
        name: F.name.trim(),
        supplierId: Number(F.supplierId),
        targetDate: F.target,
        currentStage: Number(F.stage),
        buyerId: isAdmin ? buyerId : undefined,
        note: F.note.trim(),
      });
      onPartSaved(p);
      setAdded(p);
      setF((f) => ({ ...f, name: "", supplierId: "", note: "", stage: 0 }));
      toast(`${p.partNo} added`);
    } catch (e) {
      setErr(errorMessage(e, "Could not add the part."));
    } finally {
      setSaving(false);
    }
  };

  if (!lines.length) {
    return (
      <>
        <h2>Add Part</h2>
        <div className="panel empty">
          {isAdmin ? "Add an assembly line first from the Assembly Lines tab." : "No assembly lines yet. Ask your admin to add them."}
        </div>
      </>
    );
  }

  return (
    <>
      <h2>Add Part</h2>
      <p className="sub">New items show up straight away on the buyer dashboard, the Development Tracker and the Main Dashboard.</p>
      <div className="tools" style={{ marginBottom: 12 }}>
        <button type="button" className={`btn${mode === "single" ? " p" : ""}`} onClick={() => setMode("single")}>Add single part</button>
        <button type="button" className={`btn${mode === "import" ? " p" : ""}`} onClick={() => setMode("import")}>Import from Excel</button>
      </div>
      {mode === "import" ? <ImportParts app={app} /> : (
      <>
      {added && (
        <div className="panel ok">
          Added {added.partNo} to {lines.find((l) => l.id === added.assemblyLineId)?.name}, assigned to {added.buyerName}.{" "}
          <a href="#open" onClick={(e) => { e.preventDefault(); openPart(added.id); }}>Open part</a>
        </div>
      )}
      <div className="panel">
        <div className="fm">
          <label>Assembly line
            <select value={F.lineId} onChange={(e) => setF((f) => ({ ...f, lineId: e.target.value, stage: 0 }))}>
              {lines.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.product}</option>)}
            </select>
          </label>
          <label>Part name
            <input value={F.name} onChange={set("name")} placeholder="e.g. Bracket 214" />
          </label>
          <label>Supplier
            {newSup === null ? (
              <span className="inl">
                <select value={F.supplierId} onChange={set("supplierId")}>
                  <option value="">Select supplier</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button type="button" className="btn" style={{margin: "5px 0 20px 0"}} onClick={() => setNewSup("")}>Add</button>
              </span>
            ) : (
              <span className="inl">
                <input autoFocus value={newSup} placeholder="New supplier name" onChange={(e) => setNewSup(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveSupplier()} />
                <button type="button" className="btn p" style={{margin: "5px 0 20px 0"}} onClick={saveSupplier} disabled={supSaving}>Save</button>
                <button type="button" className="btn" style={{margin: "5px 0 20px 0"}} onClick={() => setNewSup(null)}>Cancel</button>
              </span>
            )}
          </label>
          <label>Target date
            <input type="date" value={F.target} min={iso(today)} onChange={set("target")} />
          </label>
          <label>Current stage
            <select value={F.stage} onChange={set("stage")}>
              {L?.stages.map((s, i) => <option key={i} value={i}>{i + 1}. {s}</option>)}
            </select>
          </label>
          <label>Assigned buyer
            {isAdmin ? (
              <select value={buyerId} onChange={set("buyerId")}>
                {!buyers.length && <option value="">No buyers found</option>}
                {buyers.map((b) => <option key={b.orgUserId} value={b.orgUserId}>{b.name}</option>)}
              </select>
            ) : (
              <input value={me.name} disabled />
            )}
          </label>
          <label className="wide">Note (optional)
            <textarea value={F.note} onChange={set("note")} />
          </label>
        </div>
        <div className="red" style={{ marginTop: 8 }}>{err}</div>
        <div className="tools" style={{ marginTop: 10 }}>
          <button className="btn p" onClick={submit} disabled={saving}>{saving ? "Adding..." : "Add part"}</button>
        </div>
      </div>
      </>
      )}
    </>
  );
};

export default AddPart;
